import React, { useState, useEffect, useRef } from 'react'
import {
  TextField,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Box,
  Divider,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
  Snackbar,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import RefreshIcon from '@mui/icons-material/Refresh'
import Parse from 'parse'
import { useAppSelector, useAppDispatch } from '../hooks'
import authService from '../services/authService'
import { setUserData, setPlayerName, setProfileDialogOpen } from '../stores/UserStore'
import type { UserData } from '../types/user'
import store from '../stores'
import { getDisplayName } from '../utils/personName'
import {
  MAX_PROFILE_AVATAR_MB,
  PROFILE_PHOTO_ACCEPT,
  PROFILE_PHOTO_EXTENSIONS,
  profilePhotoError,
} from '../constants/profile'
import { workspaceService, fileService } from '../services/back4app'
import phaserGame from '../PhaserGame'
import ProfileAvatar, { AVATAR_PATTERN_STYLES, patternStyleOf, type AvatarPatternStyle } from './ProfileAvatar'
import { mergeUserDataWithProfile, readParseGoogleLinked } from '../account'
import { useHoldCompanyAccessReload } from '../hooks/companyAccessReloadHold'
import ConnectedAccountsPanel from './profile/ConnectedAccountsPanel'
import AccountPasswordPanel from './profile/AccountPasswordPanel'
import EmailPreferencesPanel from './profile/EmailPreferencesPanel'
import CharacterPicker from './profile/CharacterPicker'
import { MAX_DISPLAY_NAME_LENGTH } from '../constants/profile'
import PersonIcon from '@mui/icons-material/Person'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset'
import ErrorIcon from '@mui/icons-material/Error'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import GoogleIcon from '@mui/icons-material/Google'
import LinkIcon from '@mui/icons-material/Link'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

// Shared Styled components (UI Standards)
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledButton,
  StyledIconButton,
} from './CompanyManagement/styles/StyledComponents'

// Local styled pieces specific to this dialog
import {
  AvatarGrid,
  CharacterSelectionWrapper,
  CharacterSelectionHeader,
  NameDisplayPreview,
  NamePreviewTitle,
  NameDisplay,
  UploadButton,
  CustomAvatarBox,
  AvatarPreview,
  UploadInput,
} from './styles/ProfileEditDialog.styles'


interface ProfileEditDialogProps {
  open: boolean
  onClose: () => void
  forceRequired?: boolean
  onProfileComplete?: (updatedUserData?: UserData) => void
}

// Available avatar variants


// Interface to extend the UserData type with additional profile fields
interface ExtendedUserData {
  id: string
  title?: string
  contact?: string
  bio?: string
}

// Form field validation interface
interface FormErrors {
  firstName?: string
  lastName?: string
  displayName?: string
  title?: string
  contact?: string
  bio?: string
  gender?: string
  dateOfBirth?: string
}

// Gender options
const genderOptions = [
  { value: '', label: 'Select Gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-Binary' },
  { value: 'prefer-not-to-say', label: 'Prefer Not to Say' },
  { value: 'other', label: 'Other' },
]

// Tab Panel component
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`,
  }
}

const ProfileEditDialog: React.FC<ProfileEditDialogProps> = ({
  open,
  onClose,
  forceRequired = false,
  onProfileComplete,
}) => {
  const userData = useAppSelector((state) => state.user.userData)
  const dispatch = useAppDispatch()

  const [loading, setLoading] = useState(false)
  /**
   * Saving is tracked separately from loading.
   *
   * One flag used to serve both, and the tab set is gated on it — so pressing Save
   * replaced the entire dialog with a spinner and then rebuilt it, which read as the
   * dialog reloading itself every time you saved. `loading` is now only the initial
   * profile fetch; `saving` drives the button. [SC-313]
   */
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [tabValue, setTabValue] = useState(0)
  const [gameCharacter, setGameCharacter] = useState('adam')
  const fileInputRef = useRef<HTMLInputElement>(null)

  /**
   * Which profile load is current. The effect is keyed on `open`, and the load it starts has a
   * 200ms delay in front of a network call — so closing and reopening the dialog (or switching
   * user) leaves an older run in flight whose post-await writes would otherwise land on top of
   * the newer one's. Only the newest run may touch state. [SC-313]
   */
  const loadGeneration = useRef(0)

  // Profile form fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [title, setTitle] = useState('')
  const [contact, setContact] = useState('')
  const [bio, setBio] = useState('')
  const [gender, setGender] = useState<string>('')
  const [dateOfBirth, setDateOfBirth] = useState<string>('')
  const [avatarVariant, setAvatarVariant] = useState<AvatarPatternStyle>('beam')
  const [customAvatar, setCustomAvatar] = useState<string | null>(null)
  /**
   * A photo picked but not saved yet. Nothing is uploaded until Save Changes: until then the
   * dialog shows it from a local object URL (`customAvatar`), and closing discards it.
   */
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null)
  /**
   * The photo or avatar style was changed in this visit. Only then does Save write `avatar` and
   * `customAvatarUrl`: the dialog keeps a Google picture out of `customAvatar`, so writing them
   * on every save cleared a Google user's photo whenever they edited their title.
   */
  const [photoTouched, setPhotoTouched] = useState(false)
  const pendingPhotoPreviewUrl = useRef<string | null>(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null)
  const [pullingGoogleAvatar, setPullingGoogleAvatar] = useState(false)
  const [uploadSnackbarOpen, setUploadSnackbarOpen] = useState(false)
  const [uploadSnackbarMessage, setUploadSnackbarMessage] = useState('')

  // Google avatar state
  const [isGoogleUser, setIsGoogleUser] = useState(false)
  const [googleAvatarStatus, setGoogleAvatarStatus] = useState<'loading' | 'loaded' | 'error' | 'none'>('none')
  const [hasGoogleAccount, setHasGoogleAccount] = useState(false)

  // Form validation
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // My Profile stays open to suspended companies; a reactivation reload would drop unsaved edits.
  useHoldCompanyAccessReload(open)

  // Sync profile dialog open state to Redux so App never shows OptimizedLoadingScreen over this dialog
  useEffect(() => {
    dispatch(setProfileDialogOpen(open))
    return () => {
      dispatch(setProfileDialogOpen(false))
    }
  }, [open, dispatch])

  const discardPendingPhoto = () => {
    if (pendingPhotoPreviewUrl.current) {
      URL.revokeObjectURL(pendingPhotoPreviewUrl.current)
      pendingPhotoPreviewUrl.current = null
    }
    setPendingPhoto(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Closing the dialog, saved or not, drops any photo that was picked but not saved.
  useEffect(() => {
    if (!open) {
      discardPendingPhoto()
      setPhotoTouched(false)
    }
  }, [open])

  useEffect(() => () => {
    if (pendingPhotoPreviewUrl.current) URL.revokeObjectURL(pendingPhotoPreviewUrl.current)
  }, [])

  // Keyed on `open`, not just the user id: MainMenu renders this dialog
  // unconditionally and merely toggles `open`, so the component stays mounted between visits. With
  // `[userData?.id]` alone the profile was fetched once per mount and every later opening reused
  // whatever the fields happened to hold — which is how a user who had just saved a first and last
  // name could reopen the dialog to empty name fields and be blocked from changing their avatar.
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!open || !userData) return

      // Check if user has Google avatar
      const hasGoogleAvatar = userData.customAvatarUrl && 
        (userData.customAvatarUrl.includes('googleusercontent.com') || 
         userData.customAvatarUrl.includes('lh3.googleusercontent.com'))
      
      setIsGoogleUser(hasGoogleAvatar)
      setGoogleAvatarStatus(hasGoogleAvatar ? 'loading' : 'none')

      // Only an account linked to Google holds the token that fetches the photo, whatever the
      // email domain (SC-438).
      setHasGoogleAccount(Boolean(userData.googleLinked ?? readParseGoogleLinked()))

      // Pre-fill with existing userData if available (handles first login when DB fields may be empty)
      setFirstName(userData.firstName || '')
      setLastName(userData.lastName || '')
      setDisplayName(userData.displayName || '')
      setTitle(userData.title || '')
      setContact(userData.contact || '')
      setBio(userData.bio || '')
      // Load gender and DOB from userData - ensure they're set even if empty
      const userGender = (userData as any).gender
      const userDOB = userData.dateOfBirth
      setGender(userGender !== undefined && userGender !== null ? String(userGender) : '')
      setDateOfBirth(
        userDOB
          ? (typeof userDOB === 'string' 
              ? userDOB.split('T')[0] 
              : new Date(userDOB).toISOString().split('T')[0])
          : ''
      )
      setAvatarVariant(patternStyleOf(userData.avatar))
      setGameCharacter(userData.gameCharacter || 'adam')
      // Only set custom avatar if it's not a Google avatar URL
      const avatarUrl = userData.customAvatarUrl || null
      if (avatarUrl && !avatarUrl.includes('googleusercontent.com') && !avatarUrl.includes('lh3.googleusercontent.com')) {
        setCustomAvatar(avatarUrl)
        setIsGoogleUser(false)
      } else if (avatarUrl) {
        // It's a Google avatar, but we'll allow it to be replaced
        setCustomAvatar(null)
        setIsGoogleUser(true)
      } else {
        setCustomAvatar(null)
      }

      const generation = ++loadGeneration.current
      const isStale = () => generation !== loadGeneration.current

      setLoading(true)

      try {
        // Add delay to prevent system locks during profile loading
        await new Promise(resolve => setTimeout(resolve, 200))
        if (isStale()) return

        // "Always fresh" needs the cache dropped first: getCurrentProfile() returns the
        // in-memory currentProfile as-is when one is set, and never revalidates. Several writers
        // can leave that cache without a name — invitation redemption seeds it from
        // validateSession while the profile is still blank, and the createDefaultProfile path
        // assigns a raw cloud response. This dialog then pre-filled from userData (names live on
        // Profile, not _User, so they are empty there), kept the stale blanks, and refused to save
        // with "First Name and Last Name are required fields" for a user whose names were in fact
        // stored.
        authService.clearInMemoryProfileCache()
        const profile = await authService.getCurrentProfile()
        // A close-and-reopen (or a user change) starts a second run while this one is still
        // in its 200ms delay or its network call. Without this the older response applied its
        // fields over the newer ones and cleared the newer spinner — which is precisely the
        // stale-profile display that keying the effect on `open` was added to fix. [SC-313]
        if (isStale()) return

        if (profile) {
          // Only overwrite if data exists to avoid clearing prefilled names
          setFirstName(profile.firstName || firstName)
          setLastName(profile.lastName || lastName)
          setDisplayName(profile.displayName || '')
          setTitle(profile.title || '')
          setContact(profile.contact || '')
          setBio(profile.bio || '')
          // Load gender and DOB - ensure they're set even if empty
          const profileGender = (profile as any).gender
          const profileDOB = (profile as any).dateOfBirth
          setGender(profileGender !== undefined && profileGender !== null ? String(profileGender) : '')
          setDateOfBirth(
            profileDOB
              ? (typeof profileDOB === 'string'
                  ? profileDOB.split('T')[0]
                  : new Date(profileDOB).toISOString().split('T')[0])
              : ''
          )
          setAvatarVariant(patternStyleOf(profile.avatar))
          setGameCharacter(profile.gameCharacter || 'adam')
          
          // Only set custom avatar if it's not a Google avatar URL
          const profileAvatarUrl = profile.customAvatarUrl || null
          if (profileAvatarUrl && !profileAvatarUrl.includes('googleusercontent.com') && !profileAvatarUrl.includes('lh3.googleusercontent.com')) {
            setCustomAvatar(profileAvatarUrl)
            setIsGoogleUser(false)
            setGoogleAvatarStatus('none')
          } else if (profileAvatarUrl) {
            // It's a Google avatar, but we'll allow it to be replaced
            setCustomAvatar(null)
            setIsGoogleUser(true)
            setGoogleAvatarStatus('loaded')
          } else {
            setCustomAvatar(null)
            setIsGoogleUser(false)
            setGoogleAvatarStatus('none')
          }
        }
      } catch (error) {
        if (isStale()) return
        // Keep the pre-filled data from userData
        console.warn('Error loading profile data:', error)
      } finally {
        // A superseded run must not clear the current run's spinner.
        if (!isStale()) setLoading(false)
      }
    }

    fetchUserProfile()

    // Invalidate this run's post-await writes when the dialog closes or the user changes.
    return () => {
      loadGeneration.current++
    }
  }, [open, userData?.id])

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'firstName':
        return !value.trim()
          ? 'First name is required'
          : value.trim().length > 30
          ? 'First name must be less than 30 characters'
          : undefined
      case 'lastName':
        return !value.trim()
          ? 'Last name is required'
          : value.trim().length > 30
          ? 'Last name must be less than 30 characters'
          : undefined
      case 'displayName':
        return value.trim().length > MAX_DISPLAY_NAME_LENGTH
          ? `Display name must be ${MAX_DISPLAY_NAME_LENGTH} characters or less`
          : undefined
      case 'title':
        return value.trim().length > 50 ? 'Title must be less than 50 characters' : undefined
      case 'contact':
        return value.trim().length > 100
          ? 'Contact information must be less than 100 characters'
          : undefined
      case 'bio':
        return value.trim().length > 500 ? 'Bio must be less than 500 characters' : undefined
      default:
        return undefined
    }
  }

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const { name, value } = e.target
    setter(value)

    // Mark field as touched
    setTouched((prev) => ({ ...prev, [name]: true }))

    // Validate the field
    const error = validateField(name, value)
    setFormErrors((prev) => ({ ...prev, [name]: error }))
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      firstName: validateField('firstName', firstName),
      lastName: validateField('lastName', lastName),
      title: validateField('title', title),
      contact: validateField('contact', contact),
      bio: validateField('bio', bio),
    }

    setFormErrors(newErrors)

    // Return true if there are no errors and both required fields are filled
    const hasNoErrors = !Object.values(newErrors).some((error) => error !== undefined)
    const hasRequiredFields = Boolean(firstName.trim() && lastName.trim())
    
    return hasNoErrors && hasRequiredFields
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handlePullGoogleAvatar = async () => {
    if (!userData?.id || !hasGoogleAccount) {
      setError('Unable to pull Google avatar. Please ensure you have a Google account.')
      return
    }

    setPullingGoogleAvatar(true)
    setError(null)

    try {
      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 300))

      console.log('Attempting to pull Google avatar for user:', userData.id)

      // Call cloud function to pull Google avatar
      const response = await Parse.Cloud.run('pullGoogleAvatar', {
        userId: userData.id
      })

      console.log('Pull Google avatar response:', response)

      if (response.success && response.avatarUrl) {
        setCustomAvatar(response.avatarUrl)
        setPhotoTouched(true)
        setIsGoogleUser(true)
        setGoogleAvatarStatus('loaded')
        setSuccess(true)
        setTimeout(() => setSuccess(false), 3000)
        console.log('Successfully pulled Google avatar for user:', userData.id)
      } else {
        throw new Error(response.error || 'Failed to pull Google avatar')
      }
    } catch (err: any) {
      console.error('Error pulling Google avatar:', err)
      
      // Provide more specific error messages
      let errorMessage = 'Failed to pull Google avatar. Please try again.'
      
      if (err.message) {
        if (err.message.includes('expired')) {
          errorMessage = 'Your Google session has expired. Please sign in with Google again to pull your avatar.'
        } else if (err.message.includes('not found')) {
          errorMessage = 'User not found. Please refresh the page and try again.'
        } else if (err.message.includes('Google account')) {
          errorMessage = 'You need a Google account (Gmail, Googlemail, or Google.com) to pull your Google avatar.'
        } else {
          errorMessage = err.message
        }
      }
      
      setError(errorMessage)
    } finally {
      setPullingGoogleAvatar(false)
    }
  }

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First Name and Last Name are required fields. You must fill them to continue.')
      return
    }

    if (!userData) {
      setError('User data not available. Please try refreshing the page.')
      return
    }

    setSaving(true)
    setError('')

    try {
      // Add delay to prevent system locks during save
      await new Promise(resolve => setTimeout(resolve, 300))

      // The picked photo is uploaded only now. If the upload fails nothing is saved.
      let savedPhotoUrl = customAvatar
      if (pendingPhoto) {
        setUploadLoading(true)
        try {
          const extension = PROFILE_PHOTO_EXTENSIONS[pendingPhoto.type]
          const random = Math.random().toString(36).substring(2, 15)
          // `<userId>-` prefix: the cloud only deletes a replaced photo whose name carries the
          // owner's id (SC-270).
          const fileName = `${userData.id}-${random}.${extension}`
          const { data: uploadData, error: uploadError } = await fileService.uploadFile(pendingPhoto, fileName)
          if (uploadError || !uploadData?.publicUrl) {
            throw new Error(uploadError?.message || 'Failed to upload your photo. Please try again.')
          }
          savedPhotoUrl = uploadData.publicUrl
          // Point the dialog at the uploaded file now, so a retry after a failed profile save
          // reuses it instead of uploading the photo again.
          discardPendingPhoto()
          setCustomAvatar(savedPhotoUrl)
        } finally {
          setUploadLoading(false)
        }
      }

      // Update profile using the new auth service
      const updateData: any = {
        firstName: firstName,
        lastName: lastName,
        displayName: displayName.trim(),
        title: title,
        contact: contact,
        bio: bio,
        gameCharacter: gameCharacter,
        profileCompleted: true, // Mark as completed when user saves
      }

      // The stored photo is left exactly as it is unless it was changed in this visit.
      if (photoTouched) {
        updateData.avatar = savedPhotoUrl ? 'custom' : avatarVariant
        updateData.photoOptOut = !savedPhotoUrl
        if (savedPhotoUrl) {
          // If user uploaded a custom avatar, use it and clear any Google avatar references
          updateData.customAvatarUrl = savedPhotoUrl
          // Ensure we're not using Google avatar when custom avatar is set
          setIsGoogleUser(false)
          setGoogleAvatarStatus('none')
        } else {
          // Clear custom avatar URL if user removed it
          updateData.customAvatarUrl = ''
        }
      }

      // Always include gender and dateOfBirth (even if empty) to ensure they're saved
      updateData.gender = gender || ''
      updateData.dateOfBirth = dateOfBirth || null

      const { data, error } = await authService.updateProfile(userData.id, updateData)

      if (error) {
        throw new Error(error)
      }

      if (data) {
        const updatedUserData = mergeUserDataWithProfile(userData, data, {
          googleLinked: readParseGoogleLinked(),
        })

        dispatch(setUserData(updatedUserData))
        setPhotoTouched(false)

        // Show success message with more detail
        setSuccess(true)
        setSuccessMessage('Profile updated successfully! Your changes are now live across the app.')
        setTimeout(() => {
          setSuccess(false)
          setSuccessMessage('')
        }, 4000)

        // Update player name mapping in Redux store for immediate UI updates (only if in a room session)
        const shownName = getDisplayName({ ...data, username: userData.username })
        const sessionId = store.getState().user.sessionId
        if (sessionId) {
          dispatch(setPlayerName({ id: sessionId, name: shownName }))
        }

        // Player name service removed - using centralized nameplate system instead

        // Propagate updated profile data to game engine and other systems
        try {
          // Update player name in the game engine if available
          const game = phaserGame?.scene?.keys?.game as any
          if (game && game.myPlayer) {
            game.myPlayer.setPlayerName(shownName)
          }

          // Update player character if gameCharacter changed, avatar changed, or gender/DOB changed
          if (game && game.myPlayer) {
            const characterChanged = data.gameCharacter !== userData.gameCharacter
            const avatarChanged = data.customAvatarUrl !== userData.customAvatarUrl
            const genderChanged = (data as any).gender !== userData.gender
            const dobChanged = (data as any).dateOfBirth !== userData.dateOfBirth
            
            if (characterChanged || avatarChanged || genderChanged || dobChanged) {
              // Update character texture
              game.myPlayer.setPlayerTexture(data.gameCharacter)
              console.log('[ProfileEditDialog] Updated game character:', {
                characterChanged,
                avatarChanged,
                genderChanged,
                dobChanged,
                newCharacter: data.gameCharacter
              })
            }
          }

          // Broadcast profile update to other players via network if available
          if (game && game.network) {
            game.network.broadcastPlayerInfo?.()
          }
        } catch (gameError) {
          // Silently handle game engine errors
        }

        // Handle completion callback
        if (onProfileComplete) {
          setTimeout(() => {
            onProfileComplete(updatedUserData)
          }, 100)
        }

        // Close dialog if not forced
        if (!forceRequired) {
          onClose()
        }
      }
    } catch (error: any) {
      setError(error.message || 'Failed to save profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleSelectPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const invalid = profilePhotoError(file)
    if (invalid) {
      setAvatarUploadError(invalid)
      setUploadSnackbarMessage(invalid)
      setUploadSnackbarOpen(true)
      event.target.value = ''
      return
    }

    discardPendingPhoto()
    setPhotoTouched(true)
    const previewUrl = URL.createObjectURL(file)
    pendingPhotoPreviewUrl.current = previewUrl
    setPendingPhoto(file)
    setCustomAvatar(previewUrl)
    setIsGoogleUser(false)
    setGoogleAvatarStatus('none')
    setAvatarUploadError(null)
    setUploadSnackbarOpen(false)
  }

  const handleRemoveCustomAvatar = () => {
    discardPendingPhoto()
    setPhotoTouched(true)
    setCustomAvatar(null)
    setIsGoogleUser(false)
    setGoogleAvatarStatus('none')
    setAvatarUploadError(null)
  }

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const fullName = `${firstName} ${lastName}`.trim()
  const previewName = getDisplayName({ displayName, firstName, lastName, username: userData?.username })
  const googleLinkedUi = Boolean(userData?.googleLinked ?? readParseGoogleLinked())

  // Check if profile is complete (both names filled)
  const isProfileComplete = firstName.trim() && lastName.trim()

  // Calculate profile completeness
  const profileCompleteness = {
    firstName: !!firstName.trim(),
    lastName: !!lastName.trim(),
    gender: !!gender,
    dateOfBirth: !!dateOfBirth,
    avatar: !!customAvatar || !!avatarVariant,
  }
  
  const completenessPercentage = Math.round(
    (Object.values(profileCompleteness).filter(Boolean).length / Object.keys(profileCompleteness).length) * 100
  )
  
  const missingFields = [
    !profileCompleteness.firstName && 'First Name',
    !profileCompleteness.lastName && 'Last Name',
    !profileCompleteness.gender && 'Gender',
    !profileCompleteness.dateOfBirth && 'Date of Birth',
    !profileCompleteness.avatar && 'Avatar',
  ].filter(Boolean) as string[]

  // Only allow closing if not forceRequired or profile is complete
  const canClose = !forceRequired || Boolean(isProfileComplete)

  // Both dismiss affordances are inert while the profile is required, and they used to
  // look perfectly clickable — nothing said why nothing happened. Name the reason, and
  // name the fields that are actually missing rather than both every time. [SC-313]
  const missingForClose = [
    !firstName.trim() && 'first name',
    !lastName.trim() && 'last name',
  ].filter(Boolean) as string[]

  const blockedCloseReason = canClose
    ? ''
    : `Add your ${missingForClose.join(' and ')}, then choose Save Changes to continue.`

  // Check if user can pull Google avatar (has Google account but not currently using Google avatar)
  const canPullGoogleAvatar = hasGoogleAccount && !isGoogleUser

  return (
    <StyledDialog open={open} onClose={canClose ? onClose : undefined} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ color: '#f97415', fontSize: '24px' }} />
            <Typography variant="h6" sx={{ color: '#424242', fontWeight: 600 }}>
              Edit Your Profile
            </Typography>
          </Box>
          {/* Tooltip needs a non-disabled wrapper: a disabled button fires no events. */}
          <Tooltip title={blockedCloseReason} disableHoverListener={canClose}>
            <span>
              <StyledIconButton edge="end" onClick={onClose} aria-label="close" disabled={!canClose}>
                <CloseIcon />
              </StyledIconButton>
            </span>
          </Tooltip>
        </Box>
      </StyledDialogTitle>

      <StyledDialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
              border: '1px solid rgba(231, 76, 60, 0.3)',
              color: '#e74c3c',
            }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {success && successMessage && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(39, 174, 96, 0.1)',
              border: '1px solid rgba(39, 174, 96, 0.3)',
              color: '#27ae60',
            }} 
            icon={<CheckCircleIcon fontSize="inherit" />}
            onClose={() => {
              setSuccess(false)
              setSuccessMessage('')
            }}
          >
            <Typography variant="body2">{successMessage}</Typography>
          </Alert>
        )}

        {/* Profile Completeness Indicator */}
        {missingFields.length > 0 && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              border: '1px solid rgba(52, 152, 219, 0.3)',
              color: '#3498db',
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Profile Completeness: {completenessPercentage}%
            </Typography>
            <Box sx={{ mb: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={completenessPercentage} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: 'rgba(52, 152, 219, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#3498db',
                  }
                }} 
              />
            </Box>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Missing fields: {missingFields.join(', ')}
            </Typography>
          </Alert>
        )}

        {(!firstName.trim() || !lastName.trim()) && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              border: '1px solid rgba(52, 152, 219, 0.3)',
              color: '#3498db',
            }}
          >
            <Typography variant="body2">
              First Name and Last Name are required fields. You must fill them to continue.
            </Typography>
          </Alert>
        )}

        {isGoogleUser && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              border: '1px solid rgba(52, 152, 219, 0.3)',
              color: '#3498db',
            }} 
            icon={<GoogleIcon fontSize="inherit" />}
          >
            <Typography variant="body2">
              You signed in with Google. Your Google profile picture is being loaded with rate limiting protection.
            </Typography>
          </Alert>
        )}

        {canPullGoogleAvatar && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              border: '1px solid rgba(52, 152, 219, 0.3)',
              color: '#3498db',
            }} 
            icon={<GoogleIcon fontSize="inherit" />}
          >
            <Typography variant="body2">
              You have a Google account. You can pull your Google profile picture to use as your avatar.
            </Typography>
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={40} sx={{ color: '#f97415' }} />
          </Box>
        ) : (
          <>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="profile edit tabs"
              sx={{
                borderBottom: '1px solid #eeeeee',
                mb: 3,
                '& .MuiTab-root': {
                  color: '#757575',
                  '&.Mui-selected': {
                    color: '#f97415',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#f97415',
                },
              }}
            >
              <Tab
                label="Basic Info"
                {...a11yProps(0)}
                icon={<PersonIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label="Profile Avatar"
                {...a11yProps(1)}
                icon={<AccountCircleIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label="Game Character"
                {...a11yProps(2)}
                icon={<VideogameAssetIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label="Connected"
                {...a11yProps(3)}
                icon={<LinkIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label="Password & Security"
                {...a11yProps(4)}
                icon={<LockOutlinedIcon fontSize="small" />}
                iconPosition="start"
              />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Box>
                <NameDisplayPreview>
                  <NamePreviewTitle variant="caption">Name Preview</NamePreviewTitle>
                  <NameDisplay>
                    <ProfileAvatar
                      userId={userData?.id}
                      name={previewName}
                      photoUrl={customAvatar || (isGoogleUser ? userData?.customAvatarUrl : undefined)}
                      patternStyle={avatarVariant}
                      size={36}
                    />
                    {previewName}
                  </NameDisplay>
                </NameDisplayPreview>

                <TextField
                  fullWidth
                  name="firstName"
                  label="First Name"
                  value={firstName}
                  onChange={(e) => handleFieldChange(e, setFirstName)}
                  margin="normal"
                  variant="outlined"
                  error={touched.firstName && !!formErrors.firstName}
                  helperText={touched.firstName && formErrors.firstName}
                />

                <TextField
                  fullWidth
                  name="lastName"
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => handleFieldChange(e, setLastName)}
                  margin="normal"
                  variant="outlined"
                  error={touched.lastName && !!formErrors.lastName}
                  helperText={touched.lastName && formErrors.lastName}
                />

                <TextField
                  fullWidth
                  name="displayName"
                  label="Display Name (optional)"
                  value={displayName}
                  onChange={(e) => handleFieldChange(e, setDisplayName)}
                  margin="normal"
                  variant="outlined"
                  inputProps={{ maxLength: MAX_DISPLAY_NAME_LENGTH }}
                  error={touched.displayName && !!formErrors.displayName}
                  helperText={
                    (touched.displayName && formErrors.displayName) ||
                    'Shown instead of your full name. Leave empty to use your first and last name.'
                  }
                />

                <TextField
                  fullWidth
                  name="title"
                  label="Job Title"
                  value={title}
                  onChange={(e) => handleFieldChange(e, setTitle)}
                  margin="normal"
                  variant="outlined"
                  error={touched.title && !!formErrors.title}
                  helperText={touched.title && formErrors.title}
                />

                <TextField
                  fullWidth
                  name="contact"
                  label="Contact Details"
                  value={contact}
                  onChange={(e) => handleFieldChange(e, setContact)}
                  margin="normal"
                  variant="outlined"
                  error={touched.contact && !!formErrors.contact}
                  helperText={touched.contact && formErrors.contact}
                />

                <TextField
                  fullWidth
                  name="bio"
                  label="Bio"
                  value={bio}
                  onChange={(e) => handleFieldChange(e, setBio)}
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={4}
                  error={touched.bio && !!formErrors.bio}
                  helperText={(touched.bio && formErrors.bio) || `${bio.length}/500 characters`}
                />

                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel id="gender-label">Gender</InputLabel>
                  <Select
                    labelId="gender-label"
                    id="gender-select"
                    value={gender}
                    label="Gender"
                    onChange={(e) => setGender(e.target.value)}
                  >
                    {genderOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  name="dateOfBirth"
                  label="Date of Birth"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0], // Prevent future dates
                  }}
                />
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box mt={2}>
                {isGoogleUser && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 2,
                      backgroundColor: 'rgba(52, 152, 219, 0.1)',
                      border: '1px solid rgba(52, 152, 219, 0.3)',
                      color: '#3498db',
                    }} 
                    icon={<GoogleIcon fontSize="small" />}
                  >
                    <Typography variant="body2">
                      You're using your Google profile picture. You can upload a custom avatar to replace it.
                    </Typography>
                  </Alert>
                )}

                {canPullGoogleAvatar && (
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <StyledButton
                      variant="outlined"
                      startIcon={pullingGoogleAvatar ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={handlePullGoogleAvatar}
                      disabled={pullingGoogleAvatar}
                    >
                      {pullingGoogleAvatar ? 'Pulling Google Avatar...' : 'Pull Google Profile Picture'}
                    </StyledButton>
                  </Box>
                )}

                <Typography variant="subtitle1" gutterBottom>
                  Select an Avatar Style
                </Typography>
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2,
                    backgroundColor: 'rgba(52, 152, 219, 0.1)',
                    border: '1px solid rgba(52, 152, 219, 0.3)',
                    color: '#3498db',
                  }}
                >
                  <Typography variant="body2">
                    <strong>Avatar styles:</strong> without a photo, everyone sees a pattern generated just for you in the style you pick here.
                  </Typography>
                </Alert>
                <AvatarGrid container spacing={2}>
                  {AVATAR_PATTERN_STYLES.map((variant) => (
                    <Grid item key={variant} xs={4} sm={2}>
                      <Box
                        className={`avatar-item ${avatarVariant === variant ? 'selected' : ''}`}
                        onClick={() => {
                          setAvatarVariant(variant)
                          discardPendingPhoto()
                          setPhotoTouched(true)
                          setCustomAvatar(null) // Clear custom avatar when selecting a boring avatar
                          setIsGoogleUser(false)
                          setGoogleAvatarStatus('none')
                        }}
                      >
                        <ProfileAvatar userId={userData?.id} name={variant} patternStyle={variant} size={60} />
                        <Typography className="avatar-label">{variant}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </AvatarGrid>

                <Divider sx={{ my: 3 }} />

                <CustomAvatarBox>
                  <Typography variant="subtitle1" gutterBottom>
                    Upload Custom Avatar
                  </Typography>

                  {avatarUploadError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2 }}
                      onClose={() => setAvatarUploadError(null)}
                    >
                      <Typography variant="body2">{avatarUploadError}</Typography>
                    </Alert>
                  )}

                  <AvatarPreview>
                    <ProfileAvatar
                      key={customAvatar || 'pattern'}
                      userId={userData?.id}
                      name={userData?.username || 'User'}
                      photoUrl={customAvatar || (isGoogleUser ? userData?.customAvatarUrl : undefined)}
                      patternStyle={avatarVariant}
                      size={100}
                    />
                  </AvatarPreview>

                  {isGoogleUser && (
                    <Chip
                      icon={<GoogleIcon />}
                      label="Google Profile Picture"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  )}

                  <UploadInput
                    type="file"
                    ref={fileInputRef}
                    accept={PROFILE_PHOTO_ACCEPT}
                    onChange={handleSelectPhoto}
                  />

                  {uploadLoading ? (
                    <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={24} />
                      <Typography variant="caption" color="text.secondary">
                        Uploading photo...
                      </Typography>
                    </Box>
                  ) : customAvatar ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <UploadButton
                        variant="outlined"
                        color="error"
                        onClick={handleRemoveCustomAvatar}
                      >
                        Remove
                      </UploadButton>
                      <UploadButton
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={triggerFileInput}
                      >
                        Replace
                      </UploadButton>
                    </Box>
                  ) : (
                    <Box>
                      <UploadButton
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={triggerFileInput}
                      >
                        Upload Avatar
                      </UploadButton>
                    </Box>
                  )}

                  {pendingPhoto && !uploadLoading && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">Not saved yet. Choose Save Changes to use this photo.</Typography>
                    </Alert>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    JPG, PNG or WebP, up to {MAX_PROFILE_AVATAR_MB} MB
                  </Typography>
                </CustomAvatarBox>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <CharacterSelectionWrapper>
                <CharacterSelectionHeader>
                  <Typography variant="subtitle1" gutterBottom>
                    Choose Your In-Game Character
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    This character will represent you in the virtual spaces
                  </Typography>
                </CharacterSelectionHeader>

                <CharacterPicker value={gameCharacter} onChange={setGameCharacter} />
              </CharacterSelectionWrapper>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              {userData ? (
                <ConnectedAccountsPanel
                  email={userData.email}
                  googleLinked={googleLinkedUi}
                  onGoogleLinkChanged={(linked) => {
                    const ud = store.getState().user.userData
                    if (ud) {
                      dispatch(setUserData({ ...ud, googleLinked: linked }))
                    }
                  }}
                />
              ) : null}
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              {/* hasManualPassword deliberately omitted: the cached current user can be
                  stale, which mislabelled the form as "Set a password" for people who
                  already had one. The panel confirms it against the server instead. */}
              <AccountPasswordPanel googleLinked={googleLinkedUi} />
              <EmailPreferencesPanel />
            </TabPanel>
          </>
        )}
      </StyledDialogContent>

      <StyledDialogActions>
        <Tooltip title={blockedCloseReason} disableHoverListener={canClose}>
          <span>
            <StyledButton
              onClick={onClose}
              variant="outlined"
              disabled={!canClose}
            >
              Cancel
            </StyledButton>
          </span>
        </Tooltip>
        <StyledButton
          onClick={handleSave}
          variant="contained"
          disabled={saving || uploadLoading || Object.values(formErrors).some(Boolean)}
        >
          {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
        </StyledButton>
      </StyledDialogActions>

      <Snackbar
        open={uploadSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setUploadSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setUploadSnackbarOpen(false)}
          sx={{ width: '100%' }}
        >
          {uploadSnackbarMessage}
        </Alert>
      </Snackbar>
    </StyledDialog>
  )
}

export default ProfileEditDialog
